import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { FeatureFlagsService } from '@core/services/feature-flags/feature-flags.service';
import { rehydrationSuccess } from '@core/store/auth/auth.actions';
import { switchToDataspace } from '@core/store/dataspace/dataspace.actions';

import { FeatureFlagsActions } from './feature-flags.actions';

@Injectable()
export class FeatureFlagsEffects {
  private readonly actions$ = inject(Actions);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagsActions.loadActiveFeatures),
      switchMap(({ dataSpaceId }) =>
        this.featureFlagsService.listActiveFeatures$(dataSpaceId).pipe(
          map((activeFeatures) =>
            FeatureFlagsActions.loadActiveFeaturesSuccess({
              activeFeatures,
              dataSpaceId: dataSpaceId ?? null
            })
          ),
          catchError((error) =>
            of(
              FeatureFlagsActions.loadActiveFeaturesFailure({
                error: error?.errorMessage ?? 'Failed to load feature flags'
              })
            )
          )
        )
      )
    )
  );

  reloadOnDataspaceChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(switchToDataspace),
      map(({ dataspace }) =>
        FeatureFlagsActions.loadActiveFeatures({ dataSpaceId: dataspace.id })
      )
    )
  );

  // Initial load only. Subsequent dataspace changes are handled by
  // reloadOnDataspaceChange$, so we must NOT react to updateUserProfile here:
  // that action also fires on a plain profile-name edit and would reload flags
  // for the user's *default* dataspace, clobbering the active features of the
  // dataspace the user is currently working in.
  loadOnAuthReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rehydrationSuccess),
      map(({ user }) =>
        FeatureFlagsActions.loadActiveFeatures({
          dataSpaceId: user.defaultDataSpace?.id
        })
      )
    )
  );
}
